import type React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MonthlySnapshot } from "../types";

interface WealthEvolutionChartProps {
  monthlyData: MonthlySnapshot[];
}

const formatBrl = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  }).format(value);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded shadow-sm text-xs">
        <p className="font-bold mb-2">Mês {label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color }} className="mb-1">
            {entry.name}: {formatBrl(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const WealthEvolutionChart: React.FC<WealthEvolutionChartProps> = ({
  monthlyData,
}) => {
  const chartData = monthlyData
    .filter((_, i) => i % 3 === 0 || i === monthlyData.length - 1)
    .map((snapshot) => ({
      month: snapshot.month,
      soFinanciando: snapshot.propertyValue,
      financiandoInvestindo: snapshot.accumulatedWealthFinancing,
      alugandoInvestindo: snapshot.accumulatedWealthRenting,
    }));

  return (
    <div className="mt-8 mb-8">
      <h3 className="text-xl font-bold text-gray-800 mb-6 px-2 text-center md:text-left">
        Evolução Patrimonial
      </h3>
      <div className="bg-white border rounded-2xl p-4 shadow-sm">
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 20, left: 10, bottom: 25 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 10 }}
                interval="preserveStartEnd"
                minTickGap={30}
                label={{
                  value: "Meses",
                  position: "insideBottom",
                  offset: -15,
                  fontSize: 11,
                }}
              />
              <YAxis
                tickFormatter={(val) =>
                  `${(val / 1000).toLocaleString("pt-BR")}k`
                }
                width={60}
                tick={{ fontSize: 10 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                height={36}
                iconType="rect"
                wrapperStyle={{ fontSize: "12px" }}
              />
              <Line
                type="monotone"
                dataKey="soFinanciando"
                name="Só financiando"
                stroke="#dc2626"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="financiandoInvestindo"
                name="Financiando e investindo"
                stroke="#16a34a"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="alugandoInvestindo"
                name="Alugando e investindo"
                stroke="#ca8a04"
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
